import { useState, useEffect } from "react";
import { Loader2, LogOut, DollarSign, Users, HandHeart, MessageSquare, FileText, Download, Heart, Link2, Lock, KeyRound } from "lucide-react";
import { SponsorSetup } from "@/components/admin/SponsorSetup";
import { CapacityCard } from "@/components/admin/CapacityCard";

const formatApiError = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

const PRIVATE_DOCUMENTS = [
  {
    slug: "irs-501c3-answers",
    filename: "wayne-walker-foundation-irs-501c3-answers.docx",
    title: "IRS 501(c)(3) Application Answers",
    kind: "Word Document · Confidential",
    desc: "Draft narrative answers for Form 1023 (activities, distributions, revenue & expense projections). Served only through the admin-authenticated API — never exposed on the public site.",
  },
];
import { toast } from "sonner";
import { API } from "@/lib/api";
import axios from "axios";

const DOCUMENTS = [
  {
    slug: "prospectus",
    category: "Sponsor Documents",
    title: "Corporate Sponsorship Prospectus",
    kind: "Executive Sponsorship Packet · Multi-Page (PDF)",
    desc: "Polished, executive-quality prospectus for corporate decision-makers — mission, business value, sponsorship levels, benefits matrix, and commitment form.",
    file: "/wayne-walker-sponsorship-prospectus.pdf",
    view: "/prospectus",
  },
  {
    slug: "marketing-guide",
    category: "Sponsor Documents",
    title: "Marketing & Brand Partnership Guide",
    kind: "Inspirational Partnership Packet · Multi-Page (PDF)",
    desc: "Premium, story-driven guide for prospective sponsors — who we are, our mission, Wayne Walker's legacy, why partnership matters, audience, benefits, and the Founding Partner invitation.",
    file: "/wayne-walker-marketing-guide.pdf",
    view: "/marketing-guide",
  },
  {
    slug: "flyer-suite",
    category: "Sponsor Documents",
    title: "Sponsorship Flyer Series",
    kind: "Sales Flyer Suite · 8 One-Page Flyers (PDF)",
    desc: "Coordinated set of premium one-page flyers for emails, leave-behinds, and meetings — Founding Sponsor, Support Those Who Serve, Why Sponsor, Opportunities, Business Case, Founding Partner Recognition, Tournament Overview, and Exclusive Sponsorships. Download the full suite or individual flyers below.",
    file: "/wwf-sponsorship-flyer-suite.pdf",
    view: "/sales-flyers",
    extras: [
      { label: "1 · Founding Sponsor", file: "/wwf-flyer-1-founding-sponsor.pdf" },
      { label: "2 · Support Those Who Serve", file: "/wwf-flyer-2-support-those-who-serve.pdf" },
      { label: "3 · Why Sponsor?", file: "/wwf-flyer-3-why-sponsor.pdf" },
      { label: "4 · Sponsorship Opportunities", file: "/wwf-flyer-4-opportunities.pdf" },
      { label: "5 · The Business Case", file: "/wwf-flyer-5-business-case.pdf" },
      { label: "6 · Founding Partner Recognition", file: "/wwf-flyer-6-founding-partner.pdf" },
      { label: "7 · Tournament Overview", file: "/wwf-flyer-7-tournament-overview.pdf" },
      { label: "8 · Exclusive Sponsorships", file: "/wwf-flyer-8-exclusive.pdf" },
    ],
  },
  {
    slug: "exec-summary",
    category: "Planning Documents",
    title: "Executive Summary",
    kind: "Board / Planning Review · One Page (PDF)",
    desc: "Vision, event details, planning timeline, and targeted goals & outcomes for the inaugural Heroes & Legacy Golf Classic.",
    file: "/heroes-legacy-executive-summary.pdf",
    view: "/exec-summary",
  },
  {
    slug: "event-flyer",
    category: "Event Documents",
    title: "Event Flyer",
    kind: "Marketing · One Page (PDF)",
    desc: "Print-ready promotional flyer with event details, sponsorship tiers, and registration information.",
    file: "/heroes-legacy-golf-classic-flyer.pdf",
    view: "/flyer",
  },
];

const DOC_CATEGORIES = ["Sponsor Documents", "Planning Documents", "Event Documents"];

export default function Admin() {  const [token, setToken] = useState(localStorage.getItem("wwf_admin_token") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("registrations");

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/summary`, { headers: { "X-Admin-Token": t } });
      setData(res.data);
      localStorage.setItem("wwf_admin_token", t);
      setToken(t);
    } catch (err) {
      toast.error("Session expired. Please sign in again.");
      localStorage.removeItem("wwf_admin_token");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    if (!input) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: input });
      setInput("");
      await load(res.data.token);
    } catch (err) {
      toast.error(formatApiError(err, "Incorrect password."));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { headers: { "X-Admin-Token": token } }); } catch (err) { /* best effort */ }
    localStorage.removeItem("wwf_admin_token");
    setToken("");
    setData(null);
  };

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match.");
    if (pw.next.length < 10) return toast.error("New password must be at least 10 characters.");
    setChangingPw(true);
    try {
      const res = await axios.post(`${API}/admin/change-password`, { current_password: pw.current, new_password: pw.next }, { headers: { "X-Admin-Token": token } });
      localStorage.setItem("wwf_admin_token", res.data.token);
      setToken(res.data.token);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password updated.", { description: "Use your new password next time you sign in." });
    } catch (err) {
      toast.error(formatApiError(err, "Could not change password."));
    } finally {
      setChangingPw(false);
    }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/admin/send-digest`, {}, { headers: { "X-Admin-Token": token } });
      if (res.data.success) toast.success("Weekly digest sent!", { description: "Emailed to the organizer address." });
      else toast.error("Digest could not be sent — check email settings.");
    } catch (err) {
      toast.error("Failed to send digest.");
    } finally {
      setSendingDigest(false);
    }
  };

  const [downloadingDoc, setDownloadingDoc] = useState("");
  const downloadAuthed = async (key, url, filename) => {
    setDownloadingDoc(key);
    try {
      const res = await axios.get(url, { headers: { "X-Admin-Token": token }, responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error("Download failed.");
    } finally {
      setDownloadingDoc("");
    }
  };
  const downloadPrivateDoc = (slug, filename) => downloadAuthed(slug, `${API}/admin/private-docs/${slug}`, filename);
  const downloadInvoice = (s) => downloadAuthed(`inv-${s.id}`, `${API}/admin/sponsorships/${s.id}/invoice`, `WWF-Sponsorship-Invoice-${s.invoice_number || s.company}.pdf`);

  const [forgotLoading, setForgotLoading] = useState(false);
  const forgotPassword = async () => {
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/admin/forgot-password`, { origin_url: window.location.origin });
      toast.success("Reset link sent.", { description: res.data.message });
    } catch (err) {
      toast.error(formatApiError(err, "Could not send reset link."));
    } finally {
      setForgotLoading(false);
    }
  };

  if (!token || !data) {
    return (
      <section className="paper-noise bg-background min-h-[70vh] flex items-center">
        <div className="max-w-sm mx-auto px-5 w-full relative z-10" data-testid="admin-login">
          <h1 className="font-serif font-black text-3xl text-espresso">Organizer Access</h1>
          <p className="mt-2 text-muted-foreground text-sm">Enter your admin password to view submissions.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Admin password"
            className="mt-6 w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust"
          />
          <button
            data-testid="admin-login-btn"
            onClick={login}
            disabled={loading}
            className="mt-6 w-full label-font text-sm bg-rust text-primary-foreground px-6 py-3.5 border border-espresso flex items-center justify-center gap-2 hard-shadow-sm disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
          </button>
          <button
            data-testid="admin-forgot-btn"
            type="button"
            onClick={forgotPassword}
            disabled={forgotLoading}
            className="mt-5 w-full text-center text-xs text-muted-foreground hover:text-rust transition-colors disabled:opacity-60"
          >
            {forgotLoading ? "Sending reset link…" : "Forgot password? Email me a reset link"}
          </button>
        </div>
      </section>
    );
  }

  const stats = data.stats;
  const tabs = [
    { key: "registrations", label: "Registrations", count: data.registrations.length },
    { key: "sponsorships", label: "Sponsorships", count: data.sponsorships.length },
    { key: "donations", label: "Donations", count: (data.donations || []).length },
    { key: "sponsor_notify", label: "Interest List", count: (data.sponsor_notifications || []).length },
    { key: "contacts", label: "Messages", count: data.contacts.length },
    { key: "documents", label: "Documents", count: DOCUMENTS.length },
    { key: "sponsor_setup", label: "Sponsor Setup", count: null },
    { key: "settings", label: "Settings", count: null },
  ];

  return (
    <section className="paper-noise bg-background min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 relative z-10" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <h1 className="font-serif font-black text-4xl text-espresso">Organizer Dashboard</h1>
          <button data-testid="admin-logout-btn" onClick={logout} className="label-font text-xs text-espresso flex items-center gap-2 border border-border px-4 py-2 hover:bg-card"><LogOut size={14} /> Logout</button>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { icon: DollarSign, label: "Pledged / Requested", value: `$${(stats.pledged || 0).toLocaleString()}` },
            { icon: Users, label: "Registrations", value: stats.total_registrations ?? stats.paid_registrations },
            { icon: HandHeart, label: "Sponsorships", value: stats.total_sponsorships ?? stats.paid_sponsorships },
            { icon: Heart, label: "Donations", value: stats.total_donations ?? 0 },
            { icon: MessageSquare, label: "Messages", value: stats.contacts },
          ].map((s) => (
            <div key={s.label} className="border border-border bg-card p-6 hard-shadow-sm">
              <s.icon className="text-rust" size={20} />
              <div className="mt-3 font-serif font-black text-3xl text-espresso">{s.value}</div>
              <div className="label-font text-[11px] text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <CapacityCard token={token} />

        <div className="mt-10 flex gap-2 border-b border-border">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`label-font text-xs px-5 py-3 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-rust text-rust" : "border-transparent text-espresso/60 hover:text-espresso"}`}
            >
              {t.label}{t.count !== null && ` (${t.count})`}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto border border-border bg-card">
          {tab === "registrations" && (
            <table className="w-full text-sm" data-testid="admin-registrations-table">
              <thead className="bg-espresso text-cream label-font text-[11px]">
                <tr>{["Package", "Captain", "Email", "Phone", "Shirt Sizes", "Amount", "Status", "Date"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.registrations.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-4 py-3">{r.package_label}</td>
                    <td className="px-4 py-3">{r.captain_name}</td>
                    <td className="px-4 py-3">{r.email}</td>
                    <td className="px-4 py-3">{r.phone}</td>
                    <td className="px-4 py-3 text-muted-foreground">{shirtSummary(r)}</td>
                    <td className="px-4 py-3">${r.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3"><StatusPill s={r.payment_status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(r.created_at)}</td>
                  </tr>
                ))}
                {data.registrations.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No registrations yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "sponsorships" && (
            <table className="w-full text-sm" data-testid="admin-sponsorships-table">
              <thead className="bg-espresso text-cream label-font text-[11px]">
                <tr>{["Tier", "Company", "Contact", "Email", "Amount", "Status", "Invoice", "Date"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.sponsorships.map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-4 py-3">{s.tier_label}</td>
                    <td className="px-4 py-3">{s.company}</td>
                    <td className="px-4 py-3">{s.contact_name}</td>
                    <td className="px-4 py-3">{s.email}</td>
                    <td className="px-4 py-3">${s.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3"><StatusPill s={s.payment_status} /></td>
                    <td className="px-4 py-3">
                      <button
                        data-testid={`admin-sponsor-invoice-${s.id}`}
                        onClick={() => downloadInvoice(s)}
                        disabled={downloadingDoc === `inv-${s.id}`}
                        className="label-font text-[10px] border border-espresso px-2.5 py-1.5 text-espresso flex items-center gap-1.5 hover:bg-espresso hover:text-cream transition-colors disabled:opacity-60 whitespace-nowrap"
                      >
                        {downloadingDoc === `inv-${s.id}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} {s.invoice_number || "Invoice"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(s.created_at)}</td>
                  </tr>
                ))}
                {data.sponsorships.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No sponsorships yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "donations" && (
            <table className="w-full text-sm" data-testid="admin-donations-table">
              <thead className="bg-espresso text-cream label-font text-[11px]">
                <tr>{["Donor", "Email", "Phone", "Amount", "Frequency", "In Honor Of", "Status", "Date"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(data.donations || []).map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="px-4 py-3">{d.name}</td>
                    <td className="px-4 py-3">{d.email}</td>
                    <td className="px-4 py-3">{d.phone || "—"}</td>
                    <td className="px-4 py-3">${d.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 capitalize">{d.frequency || "one-time"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.in_honor_of || "—"}</td>
                    <td className="px-4 py-3"><StatusPill s={d.payment_status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(d.created_at)}</td>
                  </tr>
                ))}
                {(data.donations || []).length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No donations yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "sponsor_notify" && (
            <table className="w-full text-sm" data-testid="admin-sponsor-notify-table">
              <thead className="bg-espresso text-cream label-font text-[11px]">
                <tr>{["Name", "Company", "Email", "Date"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(data.sponsor_notifications || []).map((n) => (
                  <tr key={n.id} className="border-t border-border">
                    <td className="px-4 py-3">{n.name || "—"}</td>
                    <td className="px-4 py-3">{n.company || "—"}</td>
                    <td className="px-4 py-3">{n.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(n.created_at)}</td>
                  </tr>
                ))}
                {(data.sponsor_notifications || []).length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No interest-list signups yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "contacts" && (
            <table className="w-full text-sm" data-testid="admin-contacts-table">
              <thead className="bg-espresso text-cream label-font text-[11px]">
                <tr>{["Name", "Email", "Subject", "Message", "Date"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.contacts.map((c) => (
                  <tr key={c.id} className="border-t border-border">
                    <td className="px-4 py-3">{c.name}</td>
                    <td className="px-4 py-3">{c.email}</td>
                    <td className="px-4 py-3">{c.subject}</td>
                    <td className="px-4 py-3 max-w-xs truncate">{c.message}</td>
                    <td className="px-4 py-3 text-muted-foreground">{fmt(c.created_at)}</td>
                  </tr>
                ))}
                {data.contacts.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No messages yet.</td></tr>}
              </tbody>
            </table>
          )}

          {tab === "documents" && (
            <div className="p-6 space-y-8" data-testid="admin-documents">
              <div className="border-2 border-espresso bg-espresso text-cream p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-11 w-11 bg-rust text-primary-foreground flex items-center justify-center shrink-0"><Link2 size={20} /></span>
                  <div className="min-w-0">
                    <div className="font-serif font-bold text-lg leading-tight">Shareable Sponsor Kit</div>
                    <div className="text-[11px] text-cream/70 truncate">One link with the Prospectus, Marketing Guide &amp; Flyer Series — text or email it to any prospect.</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid="admin-copy-kit-link"
                    onClick={() => {
                      const url = `${window.location.origin}/sponsor-kit`;
                      navigator.clipboard.writeText(url).then(
                        () => toast.success("Sponsor Kit link copied!", { description: url }),
                        () => toast.error("Couldn't copy — please copy manually", { description: url })
                      );
                    }}
                    className="label-font text-xs bg-rust text-primary-foreground px-4 py-2.5 border border-cream/30 flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                  >
                    <Link2 size={14} /> Copy Link
                  </button>
                  <a
                    data-testid="admin-open-kit"
                    href="/sponsor-kit"
                    target="_blank"
                    rel="noreferrer"
                    className="label-font text-xs border border-cream/40 px-4 py-2.5 text-cream flex items-center gap-2 hover:bg-cream/10"
                  >
                    Open
                  </a>
                </div>
              </div>
              <div data-testid="admin-kit-engagement" className="grid grid-cols-2 sm:grid-cols-4 gap-3 -mt-4">
                {[
                  { label: "Kit Opens", value: stats.kit_opens ?? 0 },
                  { label: "Total Downloads", value: stats.kit_downloads ?? 0 },
                  { label: "Prospectus DL", value: (stats.kit_downloads_by_doc || {}).prospectus ?? 0 },
                  { label: "Flyer Series DL", value: (stats.kit_downloads_by_doc || {}).flyers ?? 0 },
                ].map((s) => (
                  <div key={s.label} className="border border-border bg-background px-4 py-3 text-center">
                    <div className="font-serif font-black text-2xl text-espresso leading-none">{s.value}</div>
                    <div className="label-font text-[9px] text-muted-foreground mt-1.5 tracking-[0.1em]">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 -mt-4 border border-dashed border-border bg-background px-4 py-3">
                <div className="text-xs text-muted-foreground">
                  <span className="font-semibold text-espresso">Weekly digest</span> auto-emails the organizer every Monday with this activity.
                </div>
                <button
                  data-testid="admin-send-digest"
                  onClick={sendDigest}
                  disabled={sendingDigest}
                  className="label-font text-xs bg-espresso text-cream px-4 py-2.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 shrink-0"
                >
                  {sendingDigest ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Send Digest Now
                </button>
              </div>
              <div data-testid="admin-private-docs">
                <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2 mb-4">Private Documents — Admin Only</h3>
                <div className="grid sm:grid-cols-2 gap-5">
                  {PRIVATE_DOCUMENTS.map((doc) => (
                    <div key={doc.slug} data-testid={`admin-private-doc-${doc.slug}`} className="border border-border bg-background p-6 hard-shadow-sm flex flex-col">
                      <div className="flex items-center gap-3">
                        <span className="h-11 w-11 bg-espresso text-cream flex items-center justify-center shrink-0"><Lock size={20} /></span>
                        <div>
                          <div className="font-serif font-bold text-lg text-espresso leading-tight">{doc.title}</div>
                          <div className="label-font text-[10px] text-muted-foreground mt-0.5">{doc.kind}</div>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{doc.desc}</p>
                      <div className="mt-4">
                        <button
                          data-testid={`admin-private-doc-download-${doc.slug}`}
                          onClick={() => downloadPrivateDoc(doc.slug, doc.filename)}
                          disabled={downloadingDoc === doc.slug}
                          className="label-font text-xs bg-espresso text-cream px-4 py-2.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm disabled:opacity-60"
                        >
                          {downloadingDoc === doc.slug ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {DOC_CATEGORIES.map((cat) => {
                const docs = DOCUMENTS.filter((d) => d.category === cat);
                if (docs.length === 0) return null;
                return (
                  <div key={cat} data-testid={`admin-doc-category-${cat.toLowerCase().replace(/\s/g, "-")}`}>
                    <h3 className="label-font text-xs text-rust tracking-[0.15em] border-b border-border pb-2 mb-4">{cat}</h3>
                    <div className="grid sm:grid-cols-2 gap-5">
                      {docs.map((doc) => (
                        <div key={doc.file} data-testid={`admin-doc-${doc.slug}`} className="border border-border bg-background p-6 hard-shadow-sm flex flex-col">
                          <div className="flex items-center gap-3">
                            <span className="h-11 w-11 bg-rust text-primary-foreground flex items-center justify-center shrink-0"><FileText size={20} /></span>
                            <div>
                              <div className="font-serif font-bold text-lg text-espresso leading-tight">{doc.title}</div>
                              <div className="label-font text-[10px] text-muted-foreground mt-0.5">{doc.kind}</div>
                            </div>
                          </div>
                          <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{doc.desc}</p>
                          <div className="mt-4 flex gap-2">
                            <a
                              data-testid={`admin-doc-download-${doc.slug}`}
                              href={doc.file}
                              download
                              className="label-font text-xs bg-rust text-primary-foreground px-4 py-2.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm"
                            >
                              <Download size={14} /> Download
                            </a>
                            <a
                              data-testid={`admin-doc-view-${doc.slug}`}
                              href={doc.view}
                              target="_blank"
                              rel="noreferrer"
                              className="label-font text-xs border border-espresso px-4 py-2.5 text-espresso flex items-center gap-2 hover:bg-card"
                            >
                              View
                            </a>
                          </div>
                          {doc.extras && (
                            <div data-testid={`admin-doc-extras-${doc.slug}`} className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-x-4 gap-y-1.5">
                              {doc.extras.map((ex) => (
                                <a
                                  key={ex.file}
                                  href={ex.file}
                                  download
                                  className="text-xs text-espresso hover:text-rust flex items-center gap-1.5 transition-colors"
                                >
                                  <Download size={12} className="shrink-0" /> {ex.label}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "sponsor_setup" && <SponsorSetup token={token} />}

          {tab === "settings" && (
            <div data-testid="admin-settings" className="max-w-lg">
              <div className="border border-border bg-background p-7 hard-shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 bg-espresso text-cream flex items-center justify-center shrink-0"><KeyRound size={20} /></span>
                  <div>
                    <div className="font-serif font-bold text-lg text-espresso leading-tight">Change Admin Password</div>
                    <div className="label-font text-[10px] text-muted-foreground mt-0.5">Minimum 10 characters · signs out other devices</div>
                  </div>
                </div>
                <form onSubmit={changePassword} className="mt-6 space-y-5">
                  {[
                    { key: "current", label: "Current Password", testId: "admin-pw-current" },
                    { key: "next", label: "New Password", testId: "admin-pw-new" },
                    { key: "confirm", label: "Confirm New Password", testId: "admin-pw-confirm" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="label-font text-[11px] text-espresso/70">{f.label}</label>
                      <input
                        data-testid={f.testId}
                        type="password"
                        required
                        autoComplete={f.key === "current" ? "current-password" : "new-password"}
                        value={pw[f.key]}
                        onChange={(e) => setPw({ ...pw, [f.key]: e.target.value })}
                        className="mt-1 w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust"
                      />
                    </div>
                  ))}
                  <button
                    data-testid="admin-pw-submit"
                    type="submit"
                    disabled={changingPw}
                    className="label-font text-xs bg-rust text-primary-foreground px-6 py-3.5 border border-espresso flex items-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm disabled:opacity-60"
                  >
                    {changingPw ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update Password
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatusPill({ s }) {
  const paid = s === "paid";
  const waitlist = s === "waitlist";
  const cls = paid
    ? "bg-rust text-primary-foreground border-espresso"
    : waitlist
    ? "bg-espresso text-cream border-espresso"
    : "bg-transparent text-muted-foreground border-border";
  return (
    <span className={`label-font text-[10px] px-2 py-1 border ${cls}`}>
      {s || "initiated"}
    </span>
  );
}

function shirtSummary(r) {
  const parts = [];
  if (r.captain_shirt) parts.push(`C:${r.captain_shirt}`);
  if (r.player2_shirt && r.player2) parts.push(`P2:${r.player2_shirt}`);
  if (r.player3_shirt && r.player3) parts.push(`P3:${r.player3_shirt}`);
  if (r.player4_shirt && r.player4) parts.push(`P4:${r.player4_shirt}`);
  return parts.length ? parts.join(" · ") : "—";
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}
